import { config } from './environment';
import { canLoadLiveTags, initAnalytics, pageView } from './analytics';

function injectInlineScript(content, id) {
    if (document.getElementById(id)) return;
    const script = document.createElement('script');
    script.id = id;
    script.innerHTML = content;
    document.head.appendChild(script);
};

/** Initialize GTM, GA4, Facebook Pixel, and Microsoft UET.
 * GTM and the live GA4 property load ONLY in 'live' mode (production build AND a production
 * hostname, see analytics.js). In 'test' mode gtag loads with the test property only (no GTM).
 * In dry-run nothing GA4/GTM related loads. Facebook Pixel / UET are unchanged: production only.
 */
export function initTracking() {
    // Google Analytics 4 (direct gtag.js, send_page_view:false -- page views are sent by
    // TrackingRouteListener). No-ops in dry-run.
    initAnalytics();

    if (canLoadLiveTags()) {
        // Google Tag Manager
        injectInlineScript(`
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${config.GTM_ID}');
        `, 'athos-gtm');

        // GTM noscript iframe at top of body
        if (!document.getElementById('athos-gtm-noscript')) {
            const noscript = document.createElement('noscript');
            noscript.id = 'athos-gtm-noscript';
            noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${config.GTM_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
            document.body.insertBefore(noscript, document.body.firstChild);
        }
    }

    if (!canLoadLiveTags()) return;

    // Facebook Pixel
    injectInlineScript(`
        !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
        n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
        document,'script','https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${config.FB_PIXEL_ID}');
        fbq('track', 'PageView');
    `, 'athos-fbq');

    // Microsoft UET
    injectInlineScript(`
        (function(w,d,t,r,u){var f,n,i;w[u]=w[u]||[];f=function(){var o={ti:'${config.UET_TAG_ID}',enableAutoSpaTracking:true};
        o.q=w[u];w[u]=new UET(o);w[u].push('pageLoad')};n=d.createElement(t);n.src=r;n.async=1;
        n.onload=n.onreadystatechange=function(){var s=this.readyState;
        s&&s!=='loaded'&&s!=='complete'||(f(),n.onload=n.onreadystatechange=null)};
        i=d.getElementsByTagName(t)[0];i.parentNode.insertBefore(n,i);
        })(window,document,'script','//bat.bing.com/bat.js','uetq');
    `, 'athos-uet');
};

/** Fire a virtual page view across all trackers on SPA route changes.
 * Mirrors what the .NET layout fires on every full page load: GTM virtualPageView,
 * GA4 page_view, Facebook Pixel PageView, and Microsoft UET pageLoad.
 * GA4 follows the analytics.js mode; the other trackers no-op outside production.
 */
export function trackPageView(path, title) {
    // GA4 (dry-run / test / live per analytics.js mode): page_location without query string.
    pageView(path, title);

    if (!canLoadLiveTags()) return;

    // GTM -- push a virtual pageview into the dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'virtualPageView',
        page_path: path,
        page_title: title,
    });

    // Facebook Pixel
    if (window.fbq) {
        window.fbq('track', 'PageView');
    }

    // Microsoft UET
    window.uetq = window.uetq || [];
    window.uetq.push('event', 'pageLoad', { page_path: path });
};

export class AppTracking{
    /** Land the SPA on the legacy thank-you URL after an STP application submission.
     *
     * GA4's STP conversion (annual__st___special_event_production_ap) is keyed to the page
     * path /thank-you-production-application -- the URL .NET redirected to after a submission.
     * This SPA shows its confirmation in place without changing the URL, so GA4 never saw
     * React submissions. pushState is invisible to react-router (the confirmation UI stays
     * mounted); pairing it with an explicit page_view gives GA4 the exact signal .NET
     * produced, so the existing conversion keeps tracking with no GA admin changes.
     *
     * The URL swap runs in every environment so staging/UAT can verify it; the page_view
     * itself no-ops outside production (trackPageView).
     */
    static landOnSTPThankYouUrl(){
        const path = '/thank-you-production-application';
        if(window.location.pathname !== path){
            window.history.pushState({}, '', path);
        }
        trackPageView(path, 'Application Submitted');
    };

    /** Fire STP application submission conversion events (Facebook Pixel + Microsoft UET).
     * Mirrors .NET displayShortTermMessage() exactly. No-ops outside production.
     */
    static trackSTPSubmission() {
        if (!canLoadLiveTags()) return;

        // if (window.gtag) {
        //     window.gtag('event', 'conversion_event_submit_lead_form', {
        //         event_category: 'policy_purchase',
        //         event_label: 'Short Term Production Application',
        //     });
        // }

        if (window.fbq) {
            window.fbq('track', 'SubmitApplication', {
                content_name: 'Short Term Production Application',
                content_category: 'Insurance',
                currency: 'USD',
                value: 0,
            });
        }

        window.uetq = window.uetq || [];
        window.uetq.push('event', 'lead_form_submission', {
            event_category: 'policy_purchase',
            event_label: 'Short Term Production Application',
            event_value: 0,
            transaction_id: 'T_LEAD_' + Date.now(),
        });
    };

    static submitShortTermApplication(){
        if (window.fbq) {
            window.fbq('track', 'SubmitApplication', {
                content_name: 'Short Term Production Application',
                content_category: 'Insurance',
                currency: 'USD',
                value: 0,
            });
        }
    };
    static trackShortTermProductionAppLead(){
        if (window.fbq) {
            window.fbq('track', 'Lead', {
                content_name: 'Short Term Production Application',
                application_type: 'Short Term Production'
            });
        }
    };
    static shortTermApplicationPolicyPurchase(){
        window.uetq = window.uetq || [];
        window.uetq.push('event', 'lead_form_submission', {
            event_category: 'policy_purchase',
            event_label: 'Short Term Production Application',
            event_value: 0,
            transaction_id: 'T_LEAD_' + Date.now(),
        });
    };
};

/** Fire STP application submission conversion events (Facebook Pixel + Microsoft UET).
 * Mirrors .NET displayShortTermMessage() exactly. No-ops outside production.
 */
export function trackSTPSubmission() {
    if (!canLoadLiveTags()) return;

    if (window.fbq) {
        window.fbq('track', 'SubmitApplication', {
            content_name: 'Short Term Production Application',
            content_category: 'Insurance',
            currency: 'USD',
            value: 0,
        });
    }

    window.uetq = window.uetq || [];
    window.uetq.push('event', 'lead_form_submission', {
        event_category: 'policy_purchase',
        event_label: 'Short Term Production Application',
        event_value: 0,
        transaction_id: 'T_LEAD_' + Date.now(),
    });
};
